const BASE_SECONDS = 15;
const STRUCTURE_BASE_SECONDS = 30;
const STRUCTURE_PER_10K_CHARS_SECONDS = 12;
const PDF_STRUCTURE_EXTRA_SECONDS = 45;
const PDF_EXTRA_SECONDS = 6;
const FINALIZE_SECONDS = 6;
const PER_SCENE_IMAGE_SECONDS = 8;
const PER_SCENE_VIDEO_SECONDS = 20;
const VOICEOVER_PER_SCENE_SECONDS = 8;
const REEL_DURATION_PER_DAY_SECONDS = 5;
const REEL_DURATION_BASE_SECONDS = 12;
const REEL_DURATION_MIN_SECONDS = 20;
const REEL_DURATION_MAX_SECONDS = 45;
const VOICEOVER_REEL_DURATION_FACTOR = 0.25;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Infer day count from prompt/title text before the plan is generated server-side.
 * @param {{ emailText?: string, campaignTitle?: string, inputMode?: string, fileName?: string }} params
 * @returns {number}
 */
export function inferItineraryDayCount({
  emailText = '',
  campaignTitle = '',
  inputMode = 'pdf',
  fileName = '',
} = {}) {
  const combined = `${campaignTitle}\n${emailText}\n${fileName}`;

  const dayMarkers = combined.match(/\bday\s*[-#]?\s*(\d+)\b/gi) || [];
  const dayNumbers = dayMarkers
    .map((marker) => Number.parseInt(marker.replace(/\D/g, ''), 10))
    .filter((dayNumber) => Number.isInteger(dayNumber) && dayNumber > 0 && dayNumber < 30);
  if (dayNumbers.length) {
    return clamp(Math.max(...dayNumbers), 1, 14);
  }

  const durationMatch = combined.match(/(\d+)\s*days?\b/i);
  if (durationMatch) {
    return clamp(Number.parseInt(durationMatch[1], 10), 1, 14);
  }

  if (inputMode === 'pdf') {
    return 4;
  }

  const lines = String(emailText || '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 20);
  if (lines.length >= 6) {
    return clamp(Math.ceil(lines.length / 3), 2, 7);
  }
  if (lines.length >= 3) {
    return 3;
  }
  return 2;
}

/**
 * Rough reel duration used to tune voiceover-heavy estimates.
 * @param {number} dayCount
 * @returns {number}
 */
export function estimateItineraryReelDurationSeconds(dayCount) {
  const days = clamp(Number(dayCount) || 1, 1, 14);
  return clamp(
    REEL_DURATION_BASE_SECONDS + (days * REEL_DURATION_PER_DAY_SECONDS),
    REEL_DURATION_MIN_SECONDS,
    REEL_DURATION_MAX_SECONDS,
  );
}

/**
 * Estimate draft-generation wall time from user inputs available before the API returns.
 * @param {{
 *   dayCount?: number,
 *   mediaMode?: string,
 *   usePersonalizedVoiceover?: boolean,
 *   inputMode?: string,
 *   emailText?: string,
 *   campaignTitle?: string,
 *   fileName?: string,
 * }} params
 * @returns {number}
 */
export function estimateItineraryDraftLoadingSeconds({
  dayCount,
  mediaMode = 'image',
  usePersonalizedVoiceover = true,
  inputMode = 'pdf',
  emailText = '',
  campaignTitle = '',
  fileName = '',
} = {}) {
  const resolvedDayCount = Number.isFinite(dayCount) && dayCount > 0
    ? clamp(Math.round(dayCount), 1, 14)
    : inferItineraryDayCount({ emailText, campaignTitle, inputMode, fileName });
  const sceneCount = resolvedDayCount + 2;
  const isVideo = String(mediaMode || 'image').toLowerCase() === 'video';
  const perSceneSeconds = isVideo ? PER_SCENE_VIDEO_SECONDS : PER_SCENE_IMAGE_SECONDS;
  const promptChars = String(emailText || '').trim().length;
  const structureSeconds = STRUCTURE_BASE_SECONDS
    + Math.floor(promptChars / 10000) * STRUCTURE_PER_10K_CHARS_SECONDS
    + (inputMode === 'pdf' ? PDF_STRUCTURE_EXTRA_SECONDS : 0);

  let totalSeconds = BASE_SECONDS
    + structureSeconds
    + (sceneCount * perSceneSeconds)
    + FINALIZE_SECONDS
    + (inputMode === 'pdf' ? PDF_EXTRA_SECONDS : 0);

  if (usePersonalizedVoiceover) {
    const reelDurationSec = estimateItineraryReelDurationSeconds(resolvedDayCount);
    totalSeconds += (sceneCount * VOICEOVER_PER_SCENE_SECONDS)
      + Math.round(reelDurationSec * VOICEOVER_REEL_DURATION_FACTOR);
  }

  return Math.max(30, Math.round(totalSeconds / 5) * 5);
}

/**
 * @param {number} elapsedSeconds
 * @param {number} typicalSeconds
 * @returns {string}
 */
export function resolveItineraryLoadingStage(elapsedSeconds, typicalSeconds = 45) {
  const typical = Math.max(30, Number(typicalSeconds) || 45);
  const thinkingEnd = Math.max(8, Math.round(typical * 0.18));
  const mediaEnd = Math.max(thinkingEnd + 10, Math.round(typical * 0.65));

  if (elapsedSeconds < thinkingEnd) {
    return 'Thinking about your itinerary...';
  }
  if (elapsedSeconds < mediaEnd) {
    return 'Generating scenes and matching media...';
  }
  return 'Finalizing timeline and draft tasks...';
}
